import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ToasterService } from '@app/shared/components/toaster/toaster.service';
import { GraceSettingsRequest, GraceSettingsResponse, SettingsService } from '@src/app/shared/backend/clients';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-integrations-settings',
    templateUrl: './integrations-settings.component.html',
    styleUrls: ['./integrations-settings.component.scss'],
    providers: [ConfirmationService],
    host: {
        class: 'app-integrations-settings'
    },
    standalone: false
})
export class IntegrationsSettingsComponent implements OnInit {

  hasGraceInsuranceSettings:boolean = false;
  graceInsuranceIsActive: boolean = false;
  graceInsuranceInitialStatus: boolean = false;
  graceInsuranceNewStatus: boolean = false;
  sanitizedConfirmMessage: SafeHtml | undefined;
  isLoadingIntegrationSettings: boolean = false;

  public integrationsForm: FormGroup = this.fb.group({
    graceInsurance: this.fb.group({
      isActive: [false],
      apiUrl: [''],
      secretName: [''],
      ctaUrl: [''],
      maximumItemValue: [''],
      maximumAgeOfProtection: [null]
    })
  });

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private toasterService: ToasterService,
    public settingsService: SettingsService
  ) { }

  ngOnInit(): void {
    this.loadExistingIntegrationSettings();
  }

  private async loadExistingIntegrationSettings(): Promise<void> {
    this.isLoadingIntegrationSettings = true;

    try {
      const response = await firstValueFrom(this.settingsService.apiSettingsIntegrationsGet());

      if (!response?.graceSettings) {
        this.hasGraceInsuranceSettings = false;
        this.graceInsuranceIsActive = false;
        this.graceInsuranceInitialStatus = false;
        return;
      }

      const graceSettings: GraceSettingsResponse = response.graceSettings;
      const isGraceSettingsActive = graceSettings.isActive === true;

      this.hasGraceInsuranceSettings = true;
      this.graceInsuranceIsActive = isGraceSettingsActive;
      this.graceInsuranceInitialStatus = isGraceSettingsActive;

      this.integrationsForm.patchValue({
        graceInsurance: {
          isActive: isGraceSettingsActive,
          apiUrl: graceSettings.apiUrl ?? '',
          secretName: graceSettings.secretName ?? '',
          ctaUrl: graceSettings.ctaUrl ?? '',
          maximumItemValue: graceSettings.maxPrice ?? '',
          maximumAgeOfProtection: graceSettings.maxYears ?? null
        }
      });
    } catch (error) {
      console.error(error);

      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.LOAD_ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.LOAD_ERROR_MSG')
      );
    } finally {
      this.isLoadingIntegrationSettings = false;
    }
  }

  onGraceInsuranceStateChange(event: { checked: boolean }): void {
    this.graceInsuranceNewStatus = event.checked === true;

    const selectedState = this.graceInsuranceNewStatus
      ? this.translate.instant('BOOLEANS.ACTIVATE_SWITCH').toLowerCase()
      : this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH').toLowerCase();

    const translatedMessage = this.translate.instant(
      'SYSTEM_SETTINGS.INTEGRATIONS.CONFIRM_STATE_CHANGE',
      { state: selectedState }
    );

    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    this.confirmationService.confirm({
      key: 'confirm-integration-state-change-dialog',
      header: this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.CONFIRM_STATE_CHANGE_TITLE')
    });
  }

  async onConfirmGraceInsuranceStateChange(): Promise<void> {
    try {
      if (this.graceInsuranceNewStatus) {
        await firstValueFrom(this.settingsService.apiSettingsIntegrationsGraceActivatePut());
      } else {
        await firstValueFrom(this.settingsService.apiSettingsIntegrationsGraceDeactivatePut());
      }

      this.updateGraceInsuranceState(this.graceInsuranceNewStatus);
      this.confirmationService.close();

      const messageKey = this.graceInsuranceNewStatus
        ? 'SYSTEM_SETTINGS.INTEGRATIONS.ACTIVATE_SUCCESS'
        : 'SYSTEM_SETTINGS.INTEGRATIONS.DEACTIVATE_SUCCESS';

      this.toasterService.show(
        'success',
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.STATUS_UPDATED_TITLE'),
        this.translate.instant(messageKey)
      );
    } catch (error) {
      console.error(error);

      this.graceInsuranceIsActive = this.graceInsuranceInitialStatus;
      this.confirmationService.close();

      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.STATUS_UPDATE_ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.STATUS_UPDATE_ERROR_MSG')
      );
    }
  }

  onCancelGraceInsuranceStateChange(): void {
    this.graceInsuranceIsActive = this.graceInsuranceInitialStatus;
    this.confirmationService.close();
  }

  async submitForm(): Promise<void> {
    try {
      const payload: GraceSettingsRequest = {
        apiUrl: this.integrationsForm.get('graceInsurance.apiUrl')?.value,
        secretName: this.integrationsForm.get('graceInsurance.secretName')?.value,
        ctaUrl: this.integrationsForm.get('graceInsurance.ctaUrl')?.value,
        maxPrice: this.integrationsForm.get('graceInsurance.maximumItemValue')?.value,
        maxYears: this.integrationsForm.get('graceInsurance.maximumAgeOfProtection')?.value,
      };

      await firstValueFrom(this.settingsService.apiSettingsIntegrationsGraceUpsertPut(payload));

      this.hasGraceInsuranceSettings = true;

      this.toasterService.show(
        'success',
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.UPDATE_SUCCESS_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.UPDATE_SUCCESS_MSG')
      );
    } catch (error) {
      console.error(error);

      this.toasterService.show(
        'error',
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.UPDATE_ERROR_TITLE'),
        this.translate.instant('SYSTEM_SETTINGS.INTEGRATIONS.UPDATE_ERROR_MSG')
      );
    }
  }

  private updateGraceInsuranceState(isActive: boolean): void {
    this.graceInsuranceIsActive = isActive;
    this.graceInsuranceInitialStatus = isActive;

    this.integrationsForm.patchValue({
      graceInsurance: {
        isActive
      }
    });
  }

}
